// The frontend i18n resolver — `t(key, params?)`.
//
// Looks a fully-qualified resource key (e.g. `UiKeys.Nav.Instrument`, whose
// value is the string `'Ui.Nav.Instrument'`) up in the generated value
// catalogue (`@voicedojo/backend-types`, `<Domain>Values`) for the active
// culture, falling back to the `default` (English) value when a `vi-VN` entry
// is absent. The catalogues are a projection of the backend's same resource
// JSON, so the FE copy cannot drift from the source — see
// wiki/feature-resource-generation.md §Localization.
//
// Key shape: `<Domain>.<Group>.<Name>` (dotted). The first segment selects the
// catalogue; the remainder is the catalogue's own key (catalogues key values
// WITHOUT the domain prefix). A missing key resolves to the key itself so a
// gap is visible rather than blank.
//
// `params` fills positional `{0}`/`{1}` and named `{Name}` placeholders the
// backend resource strings use (e.g. `'Validations.InvalidEmail': 'Invalid
// email address: {0}'`).

import {
  AboutValues,
  AudioValues,
  AuthValues,
  BillingValues,
  LegalValues,
  PracticeValues,
  PracticesValues,
  ProgressValues,
  PromptsOfflineValues,
  RecentPracticesValues,
  RehearsalValues,
  ReviewsValues,
  RecordingsValues,
  SystemValues,
  UiValues,
  UsersValues,
} from '@voicedojo/backend-types';
import { activeCulture, type Culture } from './culture.svelte';

/** A per-culture value catalogue: `{ default: {...}, 'vi-VN'?: {...} }`. */
type ValueCatalogue = Readonly<Record<string, Readonly<Record<string, string>>>>;

/**
 * The generated catalogues, indexed by their domain prefix. Adding a resource
 * JSON regenerates a `<Domain>Values` export; register it here so `t()` can
 * route keys to it — but every entry here is in the ROOT layout's import graph,
 * so it ships to every visitor. A catalogue confined to one route subtree
 * belongs on {@link registerCatalogue} instead (`Admin` is the standing case).
 */
const catalogues: Record<string, ValueCatalogue> = {
  About: AboutValues,
  Audio: AudioValues,
  Auth: AuthValues,
  Billing: BillingValues,
  Legal: LegalValues,
  Practice: PracticeValues,
  Practices: PracticesValues,
  Progress: ProgressValues,
  // The in-browser (WebGPU) practice prompts. English-only by product rule
  // (practice content never localizes), so it ships no culture sibling and
  // always resolves to `default`. The SERVER prompt corpus (`Prompts.json`)
  // is deliberately absent — it is `$gen:["backend"]` and never reaches the
  // browser.
  PromptsOffline: PromptsOfflineValues,
  RecentPractices: RecentPracticesValues,
  Rehearsal: RehearsalValues,
  Reviews: ReviewsValues,
  Recordings: RecordingsValues,
  System: SystemValues,
  Ui: UiValues,
  Users: UsersValues,
};

/**
 * Register a route-confined catalogue so `t()` can resolve its domain. Call it
 * from the `<script module>` of the layout rooting the subtree that owns the
 * strings: the import lands the catalogue in THAT route's chunk, and the module
 * evaluates before anything under it renders. Until then its keys resolve to
 * themselves, like any unknown domain — a visible gap, never a throw.
 */
export function registerCatalogue(domain: string, values: ValueCatalogue): void {
  catalogues[domain] = values;
}

/** Fills `{0}`/`{1}` and `{Name}` placeholders from `params`. */
export function interpolate(
  template: string,
  params: Record<string, string | number> | undefined,
): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, token: string) => {
    const value = params[token];
    return value === undefined ? match : String(value);
  });
}

/**
 * Resolves a resource key to its string in the active culture, with `default`
 * fallback. Unknown keys resolve to the key itself.
 */
export function t(
  key: string,
  params?: Record<string, string | number>,
): string {
  const dot = key.indexOf('.');
  if (dot < 0) return key;

  const domain = key.slice(0, dot);
  const localKey = key.slice(dot + 1);
  const catalogue = catalogues[domain];
  if (!catalogue) return key;

  const culture: Culture = activeCulture();
  const resolved =
    (culture !== 'default' ? catalogue[culture]?.[localKey] : undefined) ??
    catalogue.default?.[localKey];

  if (resolved === undefined) return key;
  return interpolate(resolved, params);
}

/**
 * Resolves a canonical vocabulary label to its user-facing display name in the
 * active culture — the sibling of `t()` for the generated
 * `Cues<Kind>DisplayNames` maps.
 *
 * A `$vocab` label's `displayName` is authored per culture in the same resource
 * JSON as its cue messages, so the generator emits it as a per-culture map
 * (`{ default: {...}, 'vi-VN': {...} }`) rather than as a resource key. Falls
 * back to the `default` (English) entry, then to the label itself — a label
 * with no override IS its own display name.
 *
 * Reads the active culture, so — exactly like `t()` — it is culture-reactive
 * only where it is re-evaluated: in markup, in a `$derived`, or inside a
 * function body called from one of those.
 */
export function displayName<TLabel extends string>(
  names: Readonly<Record<string, Partial<Record<TLabel, string>>>>,
  label: TLabel,
): string {
  const culture: Culture = activeCulture();
  return (
    (culture !== 'default' ? names[culture]?.[label] : undefined) ??
    names.default?.[label] ??
    label
  );
}
